import React, { useMemo } from 'react';
import { Box, Typography, Paper, Chip } from '@mui/material';
import { TrendingUp, CheckCircle } from '@mui/icons-material';
import { useConfigStore } from '../stores/useConfigStore';
import { getStageHexColor, buildStageColorMap } from '../utils/stageColors';

interface StageTransition {
  stage: string;
  timestamp: string;
  daysInStage?: number;
}

interface StageProgressionTimelineProps {
  transitions: StageTransition[];
  currentStage: string | null;
}

/**
 * Stage Progression Timeline Component
 * Shows chronological progression through onboarding/lifecycle stages
 */
export const StageProgressionTimeline: React.FC<StageProgressionTimelineProps> = ({
  transitions,
  currentStage,
}) => {
  const { config } = useConfigStore();

  const stageColorMap = useMemo(
    () => buildStageColorMap(config?.stageConfig),
    [config?.stageConfig]
  );

  if (transitions.length === 0) {
    return (
      <Typography variant="body2" color="text.secondary">
        No stage history available
      </Typography>
    );
  }

  const formatDate = (dateString: string): string => {
    const date = new Date(dateString);
    return date.toLocaleDateString('en-US', {
      month: 'short',
      day: 'numeric',
      year: 'numeric',
    });
  };

  const getRelativeTime = (dateString: string): string => {
    const date = new Date(dateString);
    const now = new Date();
    const diffMs = now.getTime() - date.getTime();
    const diffDays = Math.floor(diffMs / (1000 * 60 * 60 * 24));

    if (diffDays === 0) return 'Today';
    if (diffDays === 1) return 'Yesterday';
    if (diffDays < 7) return `${diffDays} days ago`;
    if (diffDays < 30) return `${Math.floor(diffDays / 7)} weeks ago`;
    if (diffDays < 365) return `${Math.floor(diffDays / 30)} months ago`;
    return `${Math.floor(diffDays / 365)} years ago`;
  };

  const formatStageName = (stage: string): string => {
    return stage
      .split('_')
      .map((word) => word.charAt(0).toUpperCase() + word.slice(1))
      .join(' ');
  };

  return (
    <Box>
      {transitions.map((transition, index) => {
        // Only the LAST transition can be the live one; an account can enter
        // the same stage several times (e.g. at_risk → first_batch → at_risk),
        // and the earlier visits are history, not "current".
        const isCurrentStage = index === transitions.length - 1 && transition.stage === currentStage;
        const stageColor = stageColorMap[transition.stage] || getStageHexColor(transition.stage, config?.stageConfig);

        return (
          <Box
            key={index}
            sx={{
              display: 'flex',
              gap: 2,
              mb: index < transitions.length - 1 ? 3 : 0,
              position: 'relative',
            }}
          >
            {/* Timeline line */}
            {index < transitions.length - 1 && (
              <Box
                sx={{
                  position: 'absolute',
                  left: 12,
                  top: 32,
                  bottom: -24,
                  width: 2,
                  backgroundColor: isCurrentStage ? stageColor : 'grey.300',
                }}
              />
            )}

            {/* Timeline dot */}
            <Box
              sx={{
                width: 24,
                height: 24,
                borderRadius: '50%',
                backgroundColor: isCurrentStage ? stageColor : 'grey.400',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                flexShrink: 0,
                mt: 0.5,
                zIndex: 1,
              }}
            >
              {isCurrentStage ? (
                <TrendingUp sx={{ fontSize: 14, color: 'white' }} />
              ) : (
                <CheckCircle sx={{ fontSize: 16, color: 'white' }} />
              )}
            </Box>

            {/* Content */}
            <Box flex={1}>
              <Paper
                variant="outlined"
                sx={{
                  p: 2,
                  backgroundColor: isCurrentStage ? `${stageColor}10` : 'grey.50',
                  borderColor: isCurrentStage ? stageColor : 'grey.300',
                  borderWidth: isCurrentStage ? 2 : 1,
                }}
              >
                <Box display="flex" alignItems="center" justifyContent="space-between" mb={1}>
                  <Typography variant="subtitle1" fontWeight={600}>
                    {formatStageName(transition.stage)}
                  </Typography>
                  <Box display="flex" gap={1} alignItems="center">
                    <Chip
                      label={formatStageName(transition.stage)}
                      size="small"
                      sx={{
                        bgcolor: stageColor,
                        color: '#fff',
                      }}
                    />
                    {isCurrentStage && (
                      <Chip label="Current" size="small" color="primary" variant="filled" />
                    )}
                  </Box>
                </Box>

                <Box>
                  <Box display="flex" gap={2} alignItems="center" mb={0.5}>
                    <Typography variant="body2" color="text.secondary">
                      <strong>Entered:</strong> {formatDate(transition.timestamp)}
                    </Typography>
                    <Typography variant="caption" color="text.secondary">
                      ({getRelativeTime(transition.timestamp)})
                    </Typography>
                  </Box>

                  {transition.daysInStage !== undefined && (
                    <Typography variant="body2" color="text.secondary">
                      <strong>Time in stage:</strong>{' '}
                      {transition.daysInStage === 0
                        ? 'Less than 1 day'
                        : transition.daysInStage === 1
                        ? '1 day'
                        : `${transition.daysInStage} days`}
                      {isCurrentStage && ' (and counting)'}
                    </Typography>
                  )}
                </Box>
              </Paper>
            </Box>
          </Box>
        );
      })}
    </Box>
  );
};
